/** @format */

import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import type { Paper, Question, QuestionNode } from "@question-bank/shared";
import { QuestionCard } from "./QuestionCard";
import { QuestionModal } from "./QuestionModal";
import { PDFView } from "./PdfView";
import { useBrand } from "../hooks/useBrand";
import { usePapersWithAttachments } from "../hooks/usePapersWithAttachments";
import type { PaperGroup } from "../hooks/usePaperQuestions";
import { hasAdditionalResources } from "../utils/attachments";
import { buildGroupEntry } from "../utils/paper";
import { buildParentGroupSummary } from "../utils/parentSummary";

interface AdminQuestionPreviewModalProps {
	parent: Question;
	items: Question[];
	/**
	 * The owning Paper, resolved by the parent (QB2-223). Supplies the
	 * paper-level facts (subject/qualification/tier/series/date) the preview summary
	 * needs, since a bare Question no longer carries them.
	 */
	paper?: Paper | null;
	onClose: () => void;
}

const PREVIEW_TABS = [
	{ key: "card", label: "Card" },
	{ key: "modal", label: "Question Modal" },
	{ key: "pdf", label: "Paper PDF" },
] as const;
type PreviewTabKey = (typeof PREVIEW_TABS)[number]["key"];

export const AdminQuestionPreviewModal = ({ parent, items, paper, onClose }: AdminQuestionPreviewModalProps) => {
	const { brand } = useBrand();
	const paperIdsWithAttachments = usePapersWithAttachments(brand);
	const [activeTab, setActiveTab] = useState<PreviewTabKey>("card");
	const closeRef = useRef<HTMLButtonElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);

	const summary = useMemo(
		() =>
			buildParentGroupSummary(
				items,
				paper
					? {
							subject: paper.subject,
							qualification: paper.qualification,
							tier: paper.tier,
							series: paper.series,
							date: paper.date,
						}
					: undefined,
			),
		[items, paper],
	);

	const group = useMemo<PaperGroup>(
		() => ({ entry: buildGroupEntry(summary), questions: items as QuestionNode[] }),
		[summary, items],
	);
	const hasResources = hasAdditionalResources(summary, paperIdsWithAttachments);

	useEffect(() => {
		closeRef.current?.focus();
	}, []);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) {
			return;
		}
		const trap = (e: KeyboardEvent) => {
			if (e.key !== "Tab") {
				return;
			}
			const focusable = dialog.querySelectorAll<HTMLLIElement>(
				`button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])`,
			);
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
				e.preventDefault();
				(e.shiftKey ? last : first).focus();
			}
		};
		dialog.addEventListener("keydown", trap);
		return () => dialog.removeEventListener("keydown", trap);
	}, []);

	const handleCardClickCapture = (e: MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
			onMouseDown={handleBackdropMouseDown}
		>
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="admin-preview-title"
				className="flex h-[85vh] w-full max-w-220.5 flex-col rounded-lg border border-slate-200 bg-white shadow-(--admin-shadow-raised)"
			>
				<header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
					<h2 id="admin-preview-title" className="text-base font-semibold text-(--admin-color-primary)">
						Question preview
					</h2>
					<button
						ref={closeRef}
						type="button"
						onClick={onClose}
						aria-label="Close preview"
						className="cursor-pointer rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={2}
							stroke="currentColor"
							className="size-5"
							aria-hidden="true"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
						</svg>
					</button>
				</header>
				<div
					role="tablist"
					aria-label="Preview surfaces"
					className="flex shrink-0 gap-1 border-b border-slate-200 px-6"
				>
					{PREVIEW_TABS.map(({ key, label }) => {
						const isActive = activeTab === key;
						return (
							<button
								key={key}
								id={`preview-tab-${key}`}
								role="tab"
								type="button"
								aria-selected={isActive}
								aria-controls={isActive ? `preview-panel-${key}` : undefined}
								onClick={() => setActiveTab(key)}
								className={`cursor-pointer border-b-2 px-4 py-2 text-sm font-semibold ${
									isActive
										? "border-(--admin-color-primary-strong) bg-(--admin-color-accent-bg) text-(--admin-color-primary-strong)"
										: "border-transparent text-slate-600 hover:bg-slate-50"
								}`}
							>
								{label}
							</button>
						);
					})}
				</div>
				<div
					id={`preview-panel-${activeTab}`}
					role="tabpanel"
					aria-labelledby={`preview-tab-${activeTab}`}
					className="min-h-0 flex-1 bg-(--public-color-page-bg) p-4"
				>
					{activeTab === "card" && (
						<div className="flex h-full justify-center overflow-y-auto">
							<div onClickCapture={handleCardClickCapture} className="my-auto w-full max-w-100">
								<QuestionCard question={summary} hasResources={hasResources} />
							</div>
						</div>
					)}
					{activeTab === "modal" && (
						<div className="h-full">
							<QuestionModal isEmbedded preloadedItems={items} questionId={parent.parentQuestionId} onClose={onClose} />
						</div>
					)}
					{activeTab === "pdf" && (
						<div role="region" aria-label="Paper PDF preview" tabIndex={0} className="h-full overflow-y-auto">
							<PDFView groups={[group]} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
